import { orderSourceLabel } from '@/utils/orderSource';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Trash2, Edit, AlertTriangle, FileText, Printer, Flag, Columns2, WifiOff } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { formatDeliveryDateDisplay } from '../utils/dateUtils';
import { formatPhoneNumberDisplay } from '../utils/phoneUtils';
import { formatFieldLabel } from '@/utils/fieldLabels';
import usePreparedPrint from '@/hooks/usePreparedPrint';
import { API } from '@/config/constants';
import useDocumentPopup from '@/hooks/useDocumentPopup';
import { offlineDocumentRequest } from '@/utils/offlineDocument';

export const SessionOrdersList = ({ orders, onEdit, onReview, onDelete, deletion, confirmationIdentity = order => order }) => {
  const [armedDelete, setArmedDelete] = useState(null);
  const armedRef = useRef(null);
  const deleteArmTimer = useRef(null);
  const isArmed = order => armedDelete?.order === order
    && armedDelete.receipt === confirmationIdentity(order)
    && armedDelete.origin === order.desktop_sync?.origin;
  useLayoutEffect(() => {
    const armed = armedRef.current;
    if (armed && (!orders.includes(armed.order) || armed.receipt !== confirmationIdentity(armed.order)
      || armed.origin !== armed.order.desktop_sync?.origin)) {
      clearTimeout(deleteArmTimer.current);
      armedRef.current = null;
      setArmedDelete(null);
    }
  }, [orders, confirmationIdentity]);
  const handlePrintTrifold = usePreparedPrint({ kind: 'trifold', records: orders });
  const documentKey = order => JSON.stringify([order.id, order.document_id, order.desktop_sync?.origin || 'cloud']);
  const openDocument = useDocumentPopup({ requests: orders.filter(order => order.document_id).map(order => {
    const requestedUrl = `${API}/orders/${order.id}/document?stamp=1`;
    const request = offlineDocumentRequest(requestedUrl);
    return { key: documentKey(order), requestedUrl, url: request.url, offlineCopy: request.offline,
      orderNumber: order.internal_order_number || order.order_number };
  }) });

  useEffect(
    () => () => {
      if (deleteArmTimer.current) clearTimeout(deleteArmTimer.current);
    },
    [],
  );

  const handleDelete = (order) => {
    if (deletion?.blocked(order)) return;
    if (order.desktop_sync?.origin !== 'local') {
      onDelete(order.id);
      return;
    }
    if (armedRef.current?.order !== order || armedRef.current.receipt !== confirmationIdentity(order)
      || armedRef.current.origin !== order.desktop_sync?.origin) {
      const armed = { order, receipt: confirmationIdentity(order), origin: order.desktop_sync?.origin };
      armedRef.current = armed;
      setArmedDelete(armed);
      if (deleteArmTimer.current) clearTimeout(deleteArmTimer.current);
      deleteArmTimer.current = setTimeout(
        () => { armedRef.current = null; setArmedDelete(null); },
        3000,
      );
      return;
    }
    if (deleteArmTimer.current) clearTimeout(deleteArmTimer.current);
    deleteArmTimer.current = null;
    armedRef.current = null;
    setArmedDelete(null);
    onDelete(order.id);
  };

  const handleViewDocument = order => openDocument(documentKey(order));

  return (
    <Card data-testid="orders-list-card">
      <CardHeader>
        <CardTitle>Recently Uploaded ({orders.length})</CardTitle>
        <CardDescription>
          {orders.length > 0 
            ? 'Orders from this session. Navigate to "All Orders" to view your complete order history.'
            : 'Upload documents above to extract order information. Orders will appear here for review.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <p className="text-stone-500 text-center py-8" data-testid="no-orders-message">
            No uploads this session. Upload a PDF or image to get started.
          </p>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className={`border rounded-xl p-4 transition-colors ${
                  order.desktop_sync?.origin === 'local'
                    ? 'border-orange-300 bg-orange-50 hover:bg-orange-100/70'
                    : 'hover:bg-stone-50'
                }`}
                data-testid={`order-item-${order.id}`}
                data-desktop-origin={order.desktop_sync?.origin || 'cloud'}
              >
                {/* Warning if no delivery date */}
                {!order.delivery_date && (
                  <Alert variant="destructive" className="mb-3 bg-amber-50 border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800 text-sm">Missing Delivery Date</AlertTitle>
                    <AlertDescription className="text-amber-700 text-xs">
                      This order won't appear in upcoming deliveries until a delivery date is set.
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Warning if no order accepted date */}
                {!order.order_accepted_date && (
                  <Alert variant="destructive" className="mb-3 bg-orange-50 border-orange-200">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertTitle className="text-orange-800 text-sm">Missing Order Accepted Date</AlertTitle>
                    <AlertDescription className="text-orange-700 text-xs">
                      The order acceptance date could not be extracted. Please edit this order to add it.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Low-confidence fields — the AI wasn't fully sure; nudge a review
                    before accepting. Consequential set only (same bar as the All
                    Orders badge); hidden once a human has marked it reviewed. */}
                {order.low_confidence_fields?.length > 0 && !order.reviewed_by && (
                  <Alert className="mb-3 bg-amber-50 border-amber-200" data-testid="session-low-confidence-callout">
                    <Flag className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800 text-sm">Double-check extraction accuracy on:</AlertTitle>
                    <AlertDescription className="text-amber-700 text-xs">
                      <span className="font-medium">{order.low_confidence_fields.map(formatFieldLabel).join('; ')}</span>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-stone-900" data-testid="order-recipient-name">
                        {order.recipient_name || 'Unknown Recipient'}
                      </h3>
                      {order.internal_order_number && (
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-mono rounded">
                          {order.internal_order_number}
                        </span>
                      )}
                      {order.desktop_sync?.origin === 'local' && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-orange-200 px-2 py-0.5 text-xs font-medium text-orange-900"
                          title="Unsynced order — saved on this computer and waiting to sync."
                          data-testid={`unsynced-order-badge-${order.id}`}
                        >
                          <WifiOff className="h-3.5 w-3.5" />
                          {order.internal_order_number
                            ? 'Unsynced'
                            : `Offline #${order.desktop_sync?.temporary_sequence || '?'}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {order.document_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDocument(order)}
                        title="View original uploaded document"
                        data-testid={`view-document-${order.id}`}
                      >
                        <FileText className="h-4 w-4 text-primary" />
                      </Button>
                    )}
                    {/* Review against source — desktop/wide only (needs room for two panes),
                        and only when there's a document to compare against. */}
                    {order.document_id && onReview && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onReview(order)}
                        title="Review against source document"
                        className="hidden lg:inline-flex"
                        data-testid={`review-order-${order.id}`}
                      >
                        <Columns2 className="h-4 w-4 text-primary" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePrintTrifold(order)}
                      title="Print trifold card"
                      data-testid={`print-trifold-${order.id}`}
                    >
                      <Printer className="h-4 w-4 text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(order)}
                      title="Edit order"
                      data-testid={`edit-order-${order.id}`}
                    >
                      <Edit className="h-4 w-4 text-primary" />
                    </Button>
                    <Button
                      variant={
                        isArmed(order)
                          ? 'destructive'
                          : 'ghost'
                      }
                      size="sm"
                      onClick={() => handleDelete(order)}
                      disabled={deletion?.blocked(order)}
                      title={
                        isArmed(order)
                          ? 'Click again to confirm deletion'
                          : 'Delete order'
                      }
                      aria-label={
                        isArmed(order)
                          ? 'Confirm deletion'
                          : 'Delete order'
                      }
                      data-testid={`delete-order-${order.id}`}
                    >
                      {isArmed(order) ? (
                        <>
                          <AlertTriangle className="h-4 w-4" />
                          <span className="ml-1 text-[10px] leading-none">
                            Sure?
                          </span>
                        </>
                      ) : (
                        <Trash2 className="h-4 w-4 text-red-500" />
                      )}
                    </Button>
                  </div>
                </div>
                {deletion?.status(order.id) && (
                  <div role="status" className="mb-3 text-sm text-stone-700">
                    <p>{deletion.status(order.id).state === 'pending'
                      ? deletion.status(order.id).stalled
                        ? 'Still waiting for the original request. Another deletion is blocked.'
                        : 'Order request pending.'
                      : ['local', 'delete'].includes(deletion.status(order.id).kind)
                        ? 'Deletion outcome unknown. Check the current order before trying again.'
                        : 'An earlier order decision is unresolved. Check the current order before trying again.'}</p>
                    {deletion.status(order.id).review && <p>{deletion.status(order.id).review}</p>}
                    {deletion.status(order.id).state === 'unknown' && (
                      <Button variant="outline" size="sm" onClick={() => deletion.review(order.id)}>
                        Check current order
                      </Button>
                    )}
                    {(deletion.status(order.id).retry || deletion.status(order.id).complete) && (
                      <Button variant="outline" size="sm" onClick={() => deletion.acknowledge(order)}>
                        Acknowledge current observation
                      </Button>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="md:col-span-2">
                    <span className="font-medium text-stone-700">Address: </span>
                    <span className="text-stone-900">
                      {order.recipient_street_address && order.recipient_city
                        ? `${order.recipient_street_address}, ${order.recipient_city}, ${order.recipient_state || ''} ${order.recipient_zip_code || ''}`.trim()
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-stone-700">Phone: </span>
                    <span className="text-stone-900">{formatPhoneNumberDisplay(order.recipient_phone) || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-stone-700">Source Order #: </span>
                    <span className="text-stone-900">{order.order_number || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-stone-700">Network: </span>
                    <span className="text-stone-900">{orderSourceLabel(order, 'N/A')}</span>
                  </div>
                  {order.delivery_date && (
                    <div>
                      <span className="font-medium text-stone-700">Delivery: </span>
                      <span className="text-stone-900">{formatDeliveryDateDisplay(order.delivery_date)}</span>
                    </div>
                  )}
                  {order.order_accepted_date && (
                    <div>
                      <span className="font-medium text-stone-700">Accepted: </span>
                      <span className="text-stone-900">{formatDeliveryDateDisplay(order.order_accepted_date)}</span>
                    </div>
                  )}
                  {order.card_message && (
                    <div className="md:col-span-2">
                      <span className="font-medium text-stone-700">Card Message: </span>
                      <span className="text-stone-900 italic whitespace-pre-line">"{order.card_message}"</span>
                    </div>
                  )}
                  {order.special_instructions && (
                    <div className="md:col-span-2">
                      <span className="font-medium text-stone-700">Special Instructions: </span>
                      <span className="text-stone-900">{order.special_instructions}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionOrdersList;
